import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "@/context/AuthContext";
import { useProgramContext } from "@/context/ProgramContext";
import { collection, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useDocumentData } from "react-firebase-hooks/firestore";
import { firestore } from "@/firebase";
import { raveDraftConverter, raveConfigConverter } from "@/converters";
import {
  createDraftRave,
  DraftRaveGeneralCode,
  RaveRedemptionCode,
} from "@t3/shared";
import { useSearchCode } from "@/api";
import { v4 } from "uuid";

interface LiteRaveCreateContextType {
  // State
  title: string;
  setTitle: (title: string) => void;
  selectedProgramId: string;
  setSelectedProgramId: (id: string) => void;
  recipientEmail: string;
  setRecipientEmail: (email: string) => void;
  recipientName: string;
  setRecipientName: (name: string) => void;
  redemptionCode: string;
  setRedemptionCode: (code: string) => void;
  codePoints: number;
  setCodePoints: (points: number) => void;
  validatedCodeData: RaveRedemptionCode | null;
  codeError: string | null;
  scannerOpen: boolean;
  setScannerOpen: (open: boolean) => void;
  loading: boolean;
  error: string | null;
  searchLoading: boolean;
  pointLimit: number;
  programs: any[]; // Using any[] for now to match usage, ideally typed from ProgramContext

  // Actions
  handleCodeScanned: (code: string | null) => void;
  handleCodeChange: (code: string) => void;
  handleCodeBlur: () => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
}

const LiteRaveCreateContext = createContext<
  LiteRaveCreateContextType | undefined
>(undefined);

export const useLiteRaveCreateContext = () => {
  const context = useContext(LiteRaveCreateContext);
  if (!context) {
    throw new Error(
      "useLiteRaveCreateContext must be used within a LiteRaveCreateProvider",
    );
  }
  return context;
};

export const LiteRaveCreateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { program, programs } = useProgramContext();

  const [title, setTitle] = useState("");
  const [selectedProgramId, setSelectedProgramId] = useState<string>(
    program?.id || "",
  );
  const [recipientEmail, setRecipientEmail] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [redemptionCode, setRedemptionCode] = useState("");
  const [codePoints, setCodePoints] = useState<number>(100);
  const [validatedCodeData, setValidatedCodeData] =
    useState<RaveRedemptionCode | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchCode, , searchLoading] = useSearchCode();

  // Fetch RaveConfig to get the point limit
  const raveConfigDocRef = program?.id
    ? doc(
        firestore,
        `company/${user.company}/programs/${program.id}/features/raves`,
      ).withConverter(raveConfigConverter)
    : null;
  const [raveConfig] = useDocumentData(raveConfigDocRef);

  const pointLimit = raveConfig?.liteRaveUserPointLimit || 250;

  // Set initial program when loaded
  useEffect(() => {
    if (program?.id && !selectedProgramId) {
      setSelectedProgramId(program.id);
    }
  }, [program]);

  const validateRedemptionCode = useCallback(
    async (code: string) => {
      if (!code.trim()) {
        setValidatedCodeData(null);
        setCodeError(null);
        return;
      }

      try {
        setCodeError(null);
        const result = await searchCode({ code: code.trim() });

        if (!result?.codeData) {
          setCodeError("Code not found or not available");
          setValidatedCodeData(null);
          return;
        }

        // Only allow codes in edit view (dormant and not attached to a rave)
        if (!result.editView) {
          setCodeError("This code is already attached to a rave");
          setValidatedCodeData(null);
          return;
        }

        // Double-check status is dormant
        if (result.codeData.status !== "dormant") {
          setCodeError("This code is already in use");
          setValidatedCodeData(null);
          return;
        }

        // Code is valid - store the result
        setValidatedCodeData(result.codeData);
        setCodeError(null);

        // If it's a fixed value code, set the points to the fixed value
        const codeData = result.codeData;
        if (codeData.isFixedValue && codeData.fixedValuePoints) {
          setCodePoints(codeData.fixedValuePoints);
        } else if (
          "points" in codeData &&
          codeData.points !== null &&
          typeof codeData.points === "number"
        ) {
          // If code has points but not fixed value, use those as default
          setCodePoints(codeData.points);
        }
      } catch (err: any) {
        console.error("Error validating redemption code:", err);
        setCodeError(err.message || "Invalid redemption code");
        setValidatedCodeData(null);
      }
    },
    [searchCode],
  );

  const handleCodeScanned = (code: string | null) => {
    if (code) {
      setRedemptionCode(code);
      validateRedemptionCode(code);
    }
    setScannerOpen(false);
  };

  const handleCodeChange = (code: string) => {
    setRedemptionCode(code.toUpperCase());
    // Clear validation when user changes code
    setValidatedCodeData(null);
    setCodeError(null);
  };

  const handleCodeBlur = () => {
    if (redemptionCode.trim()) {
      validateRedemptionCode(redemptionCode);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError("Title is required");
      return;
    }

    if (!selectedProgramId) {
      setError("Please select a program");
      return;
    }
    // Validate redemption code if provided
    if (redemptionCode.trim()) {
      if (!validatedCodeData) {
        setError("Please verify the redemption code before submitting");
        return;
      }

      if (codeError) {
        setError("Cannot submit with invalid redemption code");
        return;
      }

      if (codePoints < 1 || codePoints > pointLimit) {
        setError(`Points value must be between 1 and ${pointLimit}`);
        return;
      }
    }

    setLoading(true);

    try {
      // Handle redemption code if provided
      let generalCodes: Record<string, DraftRaveGeneralCode> = {};

      if (redemptionCode.trim() && validatedCodeData) {
        // Use the already validated code data
        // For fixed value codes, preserve the isFixedValue flag and use fixedValuePoints
        // For variable codes, use the points from the form
        const pointsValue = validatedCodeData.isFixedValue
          ? validatedCodeData.fixedValuePoints
          : codePoints;

        generalCodes = {
          [validatedCodeData.id]: {
            codeId: validatedCodeData.id,
            code: validatedCodeData.code || "",
            points: pointsValue,
            isFixedValue: validatedCodeData.isFixedValue,
            status: "dormant",
          },
        };
      } else if (!redemptionCode.trim()) {
        // Auto-create a new code document
        generalCodes = {
          //make up a random uuid (since there isn't a document yet)
          [v4()]: {
            codeId: null,
            code: null,
            points: codePoints,
            isFixedValue: false,
            status: "dormant",
            recipientInfo: {
              name: recipientName,
              email: recipientEmail,
            },
          },
        };
      }

      // Create rave placeholder in program features
      const raveProgramFeaturesCollectionRef = collection(
        firestore,
        `company/${user.company}/programs/${selectedProgramId}/features/raves/raves`,
      );
      const newRaveDocRef = doc(raveProgramFeaturesCollectionRef);

      await setDoc(newRaveDocRef, {
        createdAt: serverTimestamp(),
      });

      // Create draft rave
      const newDraftDocRef = doc(
        raveProgramFeaturesCollectionRef,
        `${newRaveDocRef.id}/drafts/draft`,
      ).withConverter(raveDraftConverter);

      await setDoc(
        newDraftDocRef,
        createDraftRave({
          title,
          description: "",
          content: [],
          recipientIds: [],
          recipients: {},
          draftRecipients: {},
          sender: {
            id: user.id,
            name: user.name || user.email,
            avatar: user.avatar || "",
          },
          programId: selectedProgramId,
          generalCodes,
          status: "submitted",
          type: "liteRaves",
          videoAssets: {},
        }),
      );

      // Navigate to the rave view (using company raves path)
      navigate(`/lite-raves/${newRaveDocRef.id}`);
    } catch (err: any) {
      console.error("Error creating rave:", err);
      setError(err.message || "Failed to create rave");
    } finally {
      setLoading(false);
    }
  };

  const value = {
    title,
    setTitle,
    selectedProgramId,
    setSelectedProgramId,
    recipientEmail,
    setRecipientEmail,
    recipientName,
    setRecipientName,
    redemptionCode,
    setRedemptionCode,
    codePoints,
    setCodePoints,
    validatedCodeData,
    codeError,
    scannerOpen,
    setScannerOpen,
    loading,
    error,
    searchLoading,
    pointLimit,
    programs,
    handleCodeScanned,
    handleCodeChange,
    handleCodeBlur,
    handleSubmit,
  };

  return (
    <LiteRaveCreateContext.Provider value={value}>
      {children}
    </LiteRaveCreateContext.Provider>
  );
};
